import { BHASHINI_SCRIPT_URL } from "../../../../utils/constants";
import { startSiteTranslation, connectSiteTranslation, changeSiteLanguage } from "../../../../utils/siteTranslation";
import { HeaderWrapper, GovernmentBar } from "./Header.styles";
import { useEffect, useState } from "react";
import SiteSearch from "./SiteSearch";
import skipToMainContent from "../../../../utils/skipToMainContent";
import labelTranslationFeedback from "../../../../utils/labelTranslationFeedback";
import searchLanguages from "../../../../search/searchLanguages.json";

import { useFontSize } from "../../../../context/FontSizeContext";
import { useScreenReader } from "../../../../context/ScreenReaderContext";

const BHASHINI_SCRIPT_SRC =
  BHASHINI_SCRIPT_URL;
const BHASHINI_SCRIPT_ID = "bhashini-website-translation-utility";
const BHASHINI_BRIDGE_STYLE_ID = "bhashini-header-bridge-styles";
const BHASHINI_LOADING_CLASS = "topbar-bhashini-loading";
const BHASHINI_DROPDOWN_OPEN_CLASS = "topbar-bhashini-dropdown-open";
const BHASHINI_CONTAINER_OPEN_CLASS = "topbar-bhashini-open";
const BHASHINI_TRANSIENT_CLASS = "topbar-bhashini-transient";
const BHASHINI_LANGUAGE_SELECT_EVENT = "bhashini-language-select";
const BHASHINI_OVERLAY_Z_INDEX = 30000;
const BHASHINI_DROPDOWN_Z_INDEX = BHASHINI_OVERLAY_Z_INDEX + 1;

let bhashiniPluginPromise;
let bhashiniDropdownListenerAttached = false;
let bhashiniRemoveChildGuardAttached = false;
let bhashiniLoadObserver;
let bodyChildrenBeforeBhashiniLoad = new WeakSet();

const LANGUAGE_LABELS = Object.fromEntries(
  searchLanguages.map(({ code, label }) => [code, label])
);
const normalizeLanguageCode = (languageCode = "") =>
  languageCode.toLowerCase().replace(/_/g, "-");

const getBaseLanguageCode = (languageCode = "") =>
  normalizeLanguageCode(languageCode).split("-")[0];

const rememberBhashiniLanguage = (languageCode = "en") => {
  const normalizedLanguage = normalizeLanguageCode(languageCode || "en");
  const baseLanguage = getBaseLanguageCode(normalizedLanguage);

  document.documentElement.lang = normalizedLanguage;
  document.documentElement.dir = "ltr";

  try {
    localStorage.setItem("preferredLanguage", baseLanguage);
    sessionStorage.setItem("selectedLang", baseLanguage);

    localStorage.setItem("i18nextLng", baseLanguage);
  } catch (error) {
    // Storage can be unavailable in private or restricted browser contexts.
  }

  window.dispatchEvent(
    new CustomEvent("bhashini-language-change", {
      detail: { languageCode: baseLanguage },
    })
  );
};

const applyLanguage = (language) => {
  changeSiteLanguage(language);
  rememberBhashiniLanguage(language);
  const dropdown = document.getElementById("bhashiniLanguageDropdown");
  dropdown?.classList.remove("show", "show-up", "show-down");
  if (dropdown) dropdown.style.display = "none";
  document.body.classList.remove(BHASHINI_LOADING_CLASS, BHASHINI_DROPDOWN_OPEN_CLASS);
  document.querySelector(".bhashini-plugin-container")?.classList.remove(BHASHINI_CONTAINER_OPEN_CLASS);
  document.querySelector(".bhashini-dropdown-btn")?.setAttribute("aria-expanded", "false");
  dropdown?.querySelectorAll(".language-option").forEach((option) => {
    option.setAttribute("aria-selected", String(option.getAttribute("data-value") === language));
  });
};

const attachBhashiniEnglishInterceptor = (dropdown) => {
  if (!dropdown || bhashiniDropdownListenerAttached) return;
  const select = (event) => {
    if (event.type === "keydown" && !["Enter", " "].includes(event.key)) return;
    const language = event.target.closest(".language-option")?.getAttribute("data-value");
    if (!language) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    applyLanguage(language);
  };
  dropdown.addEventListener("click", select, true);
  dropdown.addEventListener("keydown", select, true);
  bhashiniDropdownListenerAttached = true;
};

const waitForBhashiniDropdown = (attemptsLeft = 20) =>
  new Promise((resolve) => {
    const checkDropdown = () => {
      const dropdown = normalizeBhashiniPluginDom();

      if (dropdown) {
        attachBhashiniEnglishInterceptor(dropdown);
        resolve(dropdown);
        return;
      }

      if (attemptsLeft <= 0) {
        resolve(null);
        return;
      }

      attemptsLeft -= 1;
      window.setTimeout(checkDropdown, 150);
    };

    checkDropdown();
  });

const ensureBhashiniStyles = () => {
  if (document.getElementById(BHASHINI_BRIDGE_STYLE_ID)) {
    return;
  }

  const style = document.createElement("style");
  style.id = BHASHINI_BRIDGE_STYLE_ID;
  style.textContent = `
    .bhashini-plugin-container {
      position: fixed !important;
      top: 36px !important;
      right: 24px !important;
      z-index: ${BHASHINI_OVERLAY_Z_INDEX} !important;
      pointer-events: none !important;
      visibility: hidden !important;
    }

    .bhashini-plugin-container.${BHASHINI_CONTAINER_OPEN_CLASS} {
      visibility: visible !important;
    }

    body:not(.${BHASHINI_DROPDOWN_OPEN_CLASS}) [id*="bhashini"],
    body:not(.${BHASHINI_DROPDOWN_OPEN_CLASS}) [class*="bhashini"]:not(.bhashini-skip-translation):not(.bhashini-skip-mutation),
    body.${BHASHINI_LOADING_CLASS} [id*="bhashini"],
    body.${BHASHINI_LOADING_CLASS} [class*="bhashini"]:not(.bhashini-skip-translation):not(.bhashini-skip-mutation) {
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }

    .bhashini-dropdown-btn {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      width: 0 !important;
      height: 0 !important;
      padding: 0 !important;
      overflow: hidden !important;
    }

    body:not(.${BHASHINI_DROPDOWN_OPEN_CLASS}) #bhashini-translation,
    body:not(.${BHASHINI_DROPDOWN_OPEN_CLASS}) #bhashiniLanguageDropdown,
    body.${BHASHINI_LOADING_CLASS} #bhashini-translation,
    body.${BHASHINI_LOADING_CLASS} #bhashiniLanguageDropdown {
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }

    .bhashini-plugin-container #bhashini-translation {
      position: relative !important;
      display: block !important;
      width: 0 !important;
      height: 0 !important;
      padding: 0 !important;
      background: transparent !important;
      overflow: visible !important;
      pointer-events: auto !important;
      z-index: ${BHASHINI_OVERLAY_Z_INDEX} !important;
    }

    #bhashini-translation.topbar-bhashini-root {
      position: fixed !important;
      display: block !important;
      width: 0 !important;
      height: 0 !important;
      padding: 0 !important;
      background: transparent !important;
      overflow: visible !important;
      pointer-events: auto !important;
      z-index: ${BHASHINI_OVERLAY_Z_INDEX} !important;
    }

    .bhashini-plugin-container #bhashiniLanguageDropdown {
      top: 0 !important;
      right: 0 !important;
      left: auto !important;
      pointer-events: auto !important;
      visibility: visible !important;
      z-index: ${BHASHINI_DROPDOWN_Z_INDEX} !important;
    }

    #bhashiniLanguageDropdown.topbar-bhashini-dropdown {
      position: fixed !important;
      left: auto !important;
      pointer-events: auto !important;
      visibility: visible !important;
      z-index: ${BHASHINI_DROPDOWN_Z_INDEX} !important;
    }

    .bhashini-plugin-container .${BHASHINI_TRANSIENT_CLASS},
    body.${BHASHINI_LOADING_CLASS} .${BHASHINI_TRANSIENT_CLASS} {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }

    body.${BHASHINI_DROPDOWN_OPEN_CLASS} .bhashini-plugin-container #bhashiniLanguageDropdown,
    body.${BHASHINI_DROPDOWN_OPEN_CLASS} .bhashini-plugin-container #bhashini-translation,
    body.${BHASHINI_DROPDOWN_OPEN_CLASS} #bhashiniLanguageDropdown.topbar-bhashini-dropdown,
    body.${BHASHINI_DROPDOWN_OPEN_CLASS} #bhashini-translation.topbar-bhashini-root {
      visibility: visible !important;
      opacity: 1 !important;
      pointer-events: auto !important;
    }
  `;
  document.head.appendChild(style);
};

const ensureBhashiniContainer = () => {
  let container = document.querySelector(".bhashini-plugin-container");

  if (!container) {
    container = document.createElement("div");
    container.className = "bhashini-plugin-container";
    document.body.appendChild(container);
  }

  return container;
};

const isStaticBodyChild = (element, container) =>
  element === container ||
  element.id === "root" ||
  bodyChildrenBeforeBhashiniLoad.has(element) ||
  ["SCRIPT", "STYLE", "LINK", "META", "NOSCRIPT"].includes(element.tagName);

const hasBhashiniIdentity = (element) => {
  const id = element.id?.toLowerCase() || "";
  const className =
    typeof element.className === "string" ? element.className.toLowerCase() : "";

  return id.includes("bhashini") || className.includes("bhashini");
};

const isBhashiniManagedNode = (node) => {
  const element =
    node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;

  return (
    !!element &&
    (hasBhashiniIdentity(element) ||
      element.classList?.contains(BHASHINI_TRANSIENT_CLASS))
  );
};

const ensureBhashiniRemoveChildGuard = () => {
  if (bhashiniRemoveChildGuardAttached || typeof Node === "undefined") {
    return;
  }

  const nativeRemoveChild = Node.prototype.removeChild;

  Node.prototype.removeChild = function guardedRemoveChild(child) {
    if (child && child.parentNode !== this && isBhashiniManagedNode(child)) {
      return child.parentNode
        ? nativeRemoveChild.call(child.parentNode, child)
        : child;
    }

    return nativeRemoveChild.call(this, child);
  };
  bhashiniRemoveChildGuardAttached = true;
};

const normalizeBhashiniPluginDom = () => {
  const container = ensureBhashiniContainer();
  const translationRoot = document.getElementById("bhashini-translation");

  translationRoot?.classList.add("topbar-bhashini-root");

  const dropdown = document.getElementById("bhashiniLanguageDropdown");

  dropdown?.classList.add("topbar-bhashini-dropdown");

  if (document.body.classList.contains(BHASHINI_LOADING_CLASS)) {
    Array.from(document.body.children).forEach((element) => {
      if (isStaticBodyChild(element, container)) {
        return;
      }

      if (!hasBhashiniIdentity(element)) {
        element.classList.add(BHASHINI_TRANSIENT_CLASS);
      }
    });
  }

  return dropdown;
};

const startBhashiniLoadCloak = () => {
  ensureBhashiniStyles();
  ensureBhashiniContainer();

  bodyChildrenBeforeBhashiniLoad = new WeakSet(Array.from(document.body.children));
  document.body.classList.add(BHASHINI_LOADING_CLASS);
  document.body.classList.remove(BHASHINI_DROPDOWN_OPEN_CLASS);

  bhashiniLoadObserver?.disconnect();
  bhashiniLoadObserver = new MutationObserver(() => {
    normalizeBhashiniPluginDom();
  });
  bhashiniLoadObserver.observe(document.body, {
    childList: true,
  });
};

const stopBhashiniLoadCloak = () => {
  bhashiniLoadObserver?.disconnect();
  bhashiniLoadObserver = null;
  document
    .querySelectorAll(`.${BHASHINI_TRANSIENT_CLASS}`)
    .forEach((element) => element.classList.remove(BHASHINI_TRANSIENT_CLASS));
};

const waitForPaint = () =>
  new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(resolve);
    });
  });

const appendBhashiniScript = () =>
  new Promise((resolve) => {
    ensureBhashiniRemoveChildGuard();

    const script = document.createElement("script");
    script.id = BHASHINI_SCRIPT_ID;
    script.src = BHASHINI_SCRIPT_SRC;
    // No integrity hash: Bhashini updates this script's content on their end,
    // which invalidates any hardcoded SRI hash and causes the browser to block it.
    // Without isReload="false" the widget reloads the whole page to translate,
    // which then requires re-loading the plugin from scratch on the next click.
    script.setAttribute("isReload", "false");
    script.setAttribute("is-redirection", "false");
    script.setAttribute("language-detection", "false");
    // Prevent vendor startup scans before the application owns DOM updates.
    document.getElementById("root")?.classList.add("bhashini-skip-translation");
    script.onload = () => {
      connectSiteTranslation();
      document.getElementById("root")?.classList.remove("bhashini-skip-translation");
      waitForBhashiniDropdown().then(resolve);
    };
    script.onerror = () => {
      document.getElementById("root")?.classList.remove("bhashini-skip-translation");
      script.remove();
      resolve(null);
    };

    ensureBhashiniContainer().appendChild(script);
  });

const ensureBhashiniPlugin = async () => {
  ensureBhashiniRemoveChildGuard();
  ensureBhashiniStyles();
  ensureBhashiniContainer();

  const existingDropdown = document.getElementById("bhashiniLanguageDropdown");

  if (existingDropdown) {
    attachBhashiniEnglishInterceptor(existingDropdown);
    return existingDropdown;
  }

  if (bhashiniPluginPromise) {
    return bhashiniPluginPromise;
  }

  const existingScript = Array.from(document.scripts).find(
    (script) => script.src === BHASHINI_SCRIPT_SRC
  );

  // If a matching script tag already exists (with or without integrity),
  // wait for the dropdown rather than injecting a duplicate script.
  if (existingScript) {
    const dropdown = await waitForBhashiniDropdown(6);

    if (dropdown) {
      return dropdown;
    }
  }

  bhashiniPluginPromise = appendBhashiniScript().then((dropdown) => {
    if (!dropdown) {
      bhashiniPluginPromise = null;
    }

    return dropdown;
  });

  return bhashiniPluginPromise;
};

const selectBhashiniLanguage = async (languageCode) => {
  const selectedLanguage = getBaseLanguageCode(languageCode || "en");
  if (selectedLanguage === "en") {
    applyLanguage("en");
    return;
  }
  const dropdown = await ensureBhashiniPlugin();

  if (!dropdown) {
    return;
  }

  let languageOption;

  for (let attempt = 0; attempt < 20 && !languageOption; attempt += 1) {
    languageOption = Array.from(
      dropdown.querySelectorAll(".language-option"),
    ).find((option) => {
      const optionCode = getBaseLanguageCode(
        option.getAttribute("data-value") || "",
      );
      const optionLabel = option.textContent?.toLowerCase() || "";

      return (
        optionCode === selectedLanguage ||
        (selectedLanguage === "hi" &&
          (optionLabel.includes("hindi") || optionLabel.includes("हिन्दी")))
      );
    });

    if (!languageOption) {
      await new Promise((resolve) => window.setTimeout(resolve, 150));
    }
  }

  languageOption?.click();
};

const TopBar = ({ compact = false }) => {
  useEffect(() => labelTranslationFeedback(), []);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [translationStatus, setTranslationStatus] = useState("");
  useEffect(() => {
    const update = ({ detail }) => setTranslationStatus(detail.failed ? "Translation failed. Select the language to retry." : detail.loading ? "Translating…" : "");
    window.addEventListener("bhashini-translation-status", update);
    return () => window.removeEventListener("bhashini-translation-status", update);
  }, []);
  const { decreaseFontSize, resetFontSize, increaseFontSize } = useFontSize();
  const {
    isScreenReaderActive,
    isSpeechSupported,
    toggleScreenReader,
  } = useScreenReader();

  useEffect(() => {
    startSiteTranslation();
    const saved = localStorage.getItem("preferredLanguage") || "en";
    changeSiteLanguage(saved);
    if (saved !== "en") ensureBhashiniPlugin();
    try {
      setSelectedLanguage(
        getBaseLanguageCode(
          localStorage.getItem("preferredLanguage") ||
            sessionStorage.getItem("selectedLang") ||
            "en",
        ),
      );
    } catch (error) {
      setSelectedLanguage("en");
    }

    const handleLanguageChange = (event) => {
      setSelectedLanguage(getBaseLanguageCode(event.detail?.languageCode || "en"));
    };

    window.addEventListener("bhashini-language-change", handleLanguageChange);

    return () => {
      window.removeEventListener("bhashini-language-change", handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    const handleExternalLanguageSelect = (event) => {
      selectBhashiniLanguage(event.detail?.languageCode);
    };

    window.addEventListener(
      BHASHINI_LANGUAGE_SELECT_EVENT,
      handleExternalLanguageSelect,
    );

    return () => {
      window.removeEventListener(
        BHASHINI_LANGUAGE_SELECT_EVENT,
        handleExternalLanguageSelect,
      );
    };
  }, []);

  const handleSiteMapClick = () => {
    window.location.href = "/Sitemap";
  }

  const handleLanguageClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const sourceButton = event.currentTarget;
    startBhashiniLoadCloak();

    const dropdown = await ensureBhashiniPlugin();
    const pluginContainer = document.querySelector(".bhashini-plugin-container");
    const pluginButton = document.querySelector(".bhashini-dropdown-btn");

    if (!dropdown || !pluginContainer) {
      stopBhashiniLoadCloak();
      document.body.classList.remove(BHASHINI_LOADING_CLASS);
      return;
    }

    normalizeBhashiniPluginDom();

    const buttonRect = sourceButton.getBoundingClientRect();
    const dropdownTop = buttonRect.bottom + 6;
    const dropdownRight = Math.max(
      8,
      window.innerWidth - buttonRect.right
    );
    const translationRoot = document.getElementById("bhashini-translation");

    pluginContainer.style.top = `${dropdownTop}px`;
    pluginContainer.style.right = `${dropdownRight}px`;

    if (translationRoot) {
      translationRoot.style.position = "fixed";
      translationRoot.style.top = `${dropdownTop}px`;
      translationRoot.style.right = `${dropdownRight}px`;
      translationRoot.style.left = "auto";
      translationRoot.style.zIndex = `${BHASHINI_OVERLAY_Z_INDEX}`;
    }

    dropdown.style.position = "fixed";
    dropdown.style.top = `${dropdownTop}px`;
    dropdown.style.right = `${dropdownRight}px`;
    dropdown.style.left = "auto";
    dropdown.style.zIndex = `${BHASHINI_DROPDOWN_Z_INDEX}`;

    const isOpen =
      dropdown.style.display === "block" || dropdown.classList.contains("show");

    dropdown.style.display = isOpen ? "none" : "block";
    dropdown.classList.toggle("show", !isOpen);
    dropdown.classList.toggle("show-down", !isOpen);
    dropdown.classList.remove("show-up");
    pluginContainer.classList.toggle(BHASHINI_CONTAINER_OPEN_CLASS, !isOpen);
    translationRoot?.classList.toggle(BHASHINI_CONTAINER_OPEN_CLASS, !isOpen);
    await waitForPaint();
    stopBhashiniLoadCloak();
    document.body.classList.remove(BHASHINI_LOADING_CLASS);
    document.body.classList.toggle(BHASHINI_DROPDOWN_OPEN_CLASS, !isOpen);
    pluginButton?.setAttribute("aria-expanded", isOpen ? "false" : "true");
  };

  return (
    <HeaderWrapper $compact={compact}>
      <GovernmentBar $compact={compact} data-screen-reader-ignore="true">
        <div className="topbar-actions flex items-center gap-8 text-white">
          <div className="flex gap-3">
            <button onClick={decreaseFontSize} className="border border-white rounded-md px-3 py-1" aria-label="Decrease font size" title="Decrease font size">
              A-
            </button>

            <button onClick={resetFontSize} className="border border-white rounded-md px-3 py-1" aria-label="Reset font size" title="Reset font size">
              A
            </button>

            <button onClick={increaseFontSize} className="border border-white rounded-md px-3 py-1" aria-label="Increase font size" title="Increase font size">
              A+
            </button>
          </div>

          <div className="topbar-divider w-px h-8 bg-white/40" />

          <button
            type="button"
            className="topbar-link-button"
            aria-pressed={isScreenReaderActive}
            aria-label={
              isScreenReaderActive
                ? "Stop screen reader"
                : "Enable screen reader"
            }
            data-screen-reader-ignore="true"
            disabled={!isSpeechSupported}
            title={
              isSpeechSupported
                ? "Read hovered or focused content"
                : "Screen reader is not supported in this browser"
            }
            onClick={toggleScreenReader}
          >
            {isScreenReaderActive ? "Stop Screen Reader" : "Screen Reader"}
          </button>

          <div className="topbar-divider w-px h-8 bg-white/40" />
             <button
            type="button"
            className="topbar-link-button topbar-skip-link"
            onClick={handleSiteMapClick}
            title="View sitemap"
          >
            Sitemap
          </button>
 <div className="topbar-divider w-px h-8 bg-white/40" />

          <button
            type="button"
            className="topbar-link-button topbar-skip-link"
            onClick={skipToMainContent}
            title="Skip to main content"
          >
            Skip to Main Content
          </button>

          <div className="topbar-divider w-px h-8 bg-white/40" />

          <SiteSearch language={selectedLanguage} />
          <div className="topbar-divider w-px h-8 bg-white/40" />
          <button
            type="button"
            className="topbar-language-selector dont-translate bhashini-skip-translation"
            aria-label="Open language translator"
            title="Choose website language"
            data-screen-reader-ignore="true"
            onClick={handleLanguageClick}
          >
            <span className="topbar-language-icon" aria-hidden="true">
              <span className="topbar-language-devanagari">{"\u0905"}</span>
              <span className="topbar-language-english">A</span>
            </span>
            {LANGUAGE_LABELS[selectedLanguage] || "English"}
          </button>
          {translationStatus && <span role="status" className="dont-translate text-xs" translate="no">{translationStatus}</span>}
        </div>
      </GovernmentBar>
    </HeaderWrapper>
  );
};

export default TopBar;

